import React, { ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import { LastPing } from 'components/Locations/LastPing'
import { PhoneDetails } from 'components/Locations/PhoneDetails'
import { Card } from 'transfix-ui/components/Card'
import { IShipperTrackingSegment, ITrackingStatus } from 'types'
import { IUserProfileDetails, IDriverLocation, INextStop, IRoute } from 'types/Location'
import { IGeocoding } from 'types/graphqlTypes'
import { getDriverLocations, getParentDriverLocations } from 'utils/actions/driverLocations'
import { getGeoCode } from 'utils/actions/mapbox'

interface IDriverLocationProps {
  userUuid: string
  limit: 3
  includeRogue: false
  includeManualPings: true
}

interface IGeoCodeProps {
  latLng: {
    lat: string
    lng: string
  }
}

interface ILocationProps {
  userProfileDetails: IUserProfileDetails
  driverLocations: IDriverLocation[]
  features: Partial<IGeocoding>[]
  isFetching: boolean
  finalStopDistanceMiles?: number
  shipmentStatus: string
  nextStop?: Pick<INextStop, 'latitude' | 'longitude'>
  getPath?: Function
  path?: IDriverLocation[]
  uuid?: string
  shipmentId?: string
  stops: INextStop[]
  predictedRoute?: IRoute[]
  previousRoute?: IRoute[]
  trackingRequired?: boolean
  trackingStatus?: ITrackingStatus
  shipperTrackingSegment?: IShipperTrackingSegment
  children?: ReactNode
}

export function Locations({
  userProfileDetails,
  driverLocations,
  features,
  isFetching,
  finalStopDistanceMiles,
  shipmentStatus,
  nextStop,
  getPath,
  path,
  uuid,
  shipmentId,
  stops = [],
  predictedRoute = [],
  previousRoute = [],
  trackingRequired,
  trackingStatus,
  shipperTrackingSegment,
  children,
}: ILocationProps) {
  const dispatch = useDispatch()
  const dispatchGetDriverLocations = (...params: [IDriverLocationProps]) =>
    dispatch(getDriverLocations(...params))
  const dispatchGetParentDriverLocations = (...params: [IDriverLocationProps]) =>
    dispatch(getParentDriverLocations(...params))
  const dispatchGetGeoCode = (...params: [IGeoCodeProps]) => dispatch(getGeoCode(...params))

  return (
    <Card title='Last Location' className='mb4'>
      <div className='mb2'>
        <LastPing
          userProfileDetails={userProfileDetails}
          getGeoCode={dispatchGetGeoCode}
          getDriverLocations={dispatchGetDriverLocations}
          getParentDriverLocations={dispatchGetParentDriverLocations}
          driverLocations={driverLocations}
          fetching={isFetching}
          features={features}
          milesToGo={finalStopDistanceMiles}
          getPath={getPath}
          path={path}
          shipmentUuid={uuid}
          shipmentId={shipmentId}
          stops={stops}
          predictedRoute={predictedRoute}
          previousRoute={previousRoute}
          trackingRequired={trackingRequired}
          trackingStatus={trackingStatus}
          shipperTrackingSegment={shipperTrackingSegment}
        />
      </div>
      {children}
      <div className='mb2'>
        <PhoneDetails
          userProfileDetails={userProfileDetails}
          driverLocations={driverLocations}
          nextStop={nextStop}
          shipmentStatus={shipmentStatus}
        />
      </div>
      <p>
        <a
          href={`${__ROOT__}/users/${
            userProfileDetails.parentUserId || userProfileDetails.id
          }/driver-locations`}
          target='_blank'
          rel='noopener noreferrer'>
          View all driver locations
        </a>
      </p>
    </Card>
  )
}
